import React, { useContext, useEffect, useState } from 'react';
import { useStyletron } from 'baseui';
import { DatePicker } from 'baseui/datepicker';
import { Drawer } from 'baseui/drawer';
import { FormControl } from 'baseui/form-control';
import { Heading, HeadingLevel } from 'baseui/heading';
import { Input } from 'baseui/input';
import { Pagination } from 'baseui/pagination';
import { Select } from 'baseui/select';
import { Skeleton } from 'baseui/skeleton';
import { faFilter } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getJurisdictions, getOpsLeads } from '@gtintel/platform-api';
import { Button, ButtonRow, ScrollToTopButton } from '@gtintel/platform-atoms';
import { useDebounce } from '@gtintel/platform-utils';
import { RequestContext } from '../ProjectApp/context';
import { RequestContextProvider } from './RequestContext';
import { ProjectAssignmentsTable } from './RequestTable';
const StatusChoices = [{
  label: 'Pending',
  id: '1'
}, {
  label: 'Matched',
  id: '3'
}, {
  label: 'Activated',
  id: '4'
}, {
  label: 'Completed',
  id: '5'
}, {
  label: 'Closed',
  id: '6'
}];
const AssignmentBody = () => {
  const [css] = useStyletron();
  const [isLoading, setIsLoading] = useState(false);
  const {
    clearFilterDateAfter,
    clearFilterDateBefore,
    currentPage,
    editSearchTerm,
    filter,
    filterStatuses,
    filterJurisdictions,
    filterOpsLeads,
    getNext,
    getPrev,
    listProjects,
    searchTerm,
    search,
    setFilterDateAfter,
    setFilterDateBefore,
    setFilterJurisdictions,
    totalPages
  } = useContext(RequestContext);
  const [term, setTerm] = useState(searchTerm());
  const [statusFilters, setStatusFilters] = useState([]);
  const [page, setPage] = useState(1);
  const [dateRangeValue, setDateRangeValue] = useState([new Date()]);
  const [isOpen, setIsOpen] = useState(false);
  const [jurisdictions, setJurisdictions] = useState([]);
  const [selectedJurisdictions, setSelectedJurisdictions] = useState([]);
  const [opsLeads, setOpsLeads] = useState([]);
  const [selectedOpsLeads, setSelectedOpsLeads] = useState([]);

  // Setting these in the declarations above caused some of them to sometimes be empty but directly setting them here in
  // the useEffect call stops that. Still not 100% sure why, likely a rendering order problem.
  useEffect(() => {
    setStatusFilters(filterStatuses());
    setSelectedJurisdictions(filterJurisdictions());
    setSelectedOpsLeads(filterOpsLeads());
    setPage(currentPage());
    setTerm(searchTerm());
    getJurisdictions().then(js => {
      setJurisdictions(js.map(j => ({
        label: j.name,
        id: j.id
      })));
    });
    getOpsLeads().then(ops => {
      setOpsLeads(ops.map(o => ({
        label: o.full_name,
        id: String(o.id)
      })));
    });
  }, []);
  const paginate = page => {
    setIsLoading(true);
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
    if (page < Number(currentPage())) {
      getPrev(page);
    } else {
      getNext(page);
    }
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  };
  const debouncedSearch = useDebounce(search, 500);
  const projects = listProjects();
  const setSearchFilter = searchTerm => {
    localStorage.setItem('searchTerm', searchTerm);
    setTerm(searchTerm);
    editSearchTerm(searchTerm);
  };
  const setStatusFilter = statuses => {
    localStorage.setItem('filterStatuses', JSON.stringify(statuses));
    setStatusFilters(statuses);
  };
  const setOpsLeadFilter = opsLeads => {
    localStorage.setItem('filterOpsLeads', JSON.stringify(opsLeads));
    setSelectedOpsLeads(opsLeads);
  };
  const setJurisdictionFilter = jurisdictions => {
    localStorage.setItem('filterJurisdictions', JSON.stringify(jurisdictions));
    setFilterJurisdictions(jurisdictions);
  };
  const setDateFilter = dateRange => {
    if (dateRange.length === 0) {
      localStorage.removeItem('dateAfter');
      localStorage.removeItem('dateBefore');
      setDateRangeValue(dateRange);
      return;
    }
    localStorage.setItem('dateAfter', dateRange[0].toISOString());
    dateRange.length > 1 && localStorage.setItem('dateBefore', dateRange[1].toISOString());
    setDateRangeValue(dateRange);
    setFilterDateAfter(dateRange[0]);
    dateRange.length > 1 && setFilterDateBefore(dateRange[1]);
  };
  const clearFilters = () => {
    setIsLoading(true);
    clearFilterDateAfter();
    clearFilterDateBefore();
    localStorage.removeItem('searchTerm');
    localStorage.removeItem('filterStatuses');
    localStorage.removeItem('filterJurisdictions');
    localStorage.removeItem('filterOpsLeads');
    setSearchFilter('');
    setStatusFilter([]);
    setJurisdictionFilter([]);
    setOpsLeadFilter([]);
    setDateFilter([]);
    doFiltering();
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  };
  const doFiltering = () => {
    filter();
  };
  const containerStyles = {
    display: 'flex',
    flexFlow: 'column wrap',
    padding: '14px 28px',
    width: '100%'
  };
  return <div className={css({
    display: 'flex',
    flexFlow: 'column nowrap',
    width: '100%'
  })}>
      <div className={css({
      alignItems: 'center',
      display: 'flex',
      flexFlow: 'row no wrap',
      gap: '8px',
      marginBottom: '24px'
    })}>
        <Input value={term} onChange={e => {
        setSearchFilter(e.target.value);
        setIsLoading(true);
        debouncedSearch();
        setTimeout(() => {
          setIsLoading(false);
        }, 2000);
      }} placeholder="Search for project name, client name, subject name, partner name or email..." overrides={{
        Root: {
          style: {
            width: '100%'
          }
        }
      }} />
        <Button testId="assignment-filter-button" onClick={() => setIsOpen(true)} kind="primary">
          <FontAwesomeIcon icon={faFilter} size="lg" className={css({
          color: 'white !important'
        })} />
        </Button>
      </div>

      {isLoading ? <section className={css(containerStyles)}>
          <Skeleton autoSizeRows animation rows={5} height="500px" width="100%" />
        </section> : <section className={css(containerStyles)}>
          {projects.map(project => {
        return <ProjectAssignmentsTable project={project} />;
      })}
        </section>}

      <Pagination numPages={totalPages()} currentPage={page} onPageChange={({
      nextPage
    }) => {
      const page = Math.min(Math.max(nextPage, 1), totalPages());
      setPage(page);
      paginate(page);
    }} />

      <ScrollToTopButton />
      <Drawer isOpen={isOpen} autoFocus onClose={() => setIsOpen(false)} overrides={{
      Root: {
        props: {
          'data-testid': 'drawer'
        }
      },
      DrawerContainer: {
        style: {
          paddingTop: '64px'
        }
      },
      Close: {
        style: {
          top: '77px'
        }
      }
    }}>
        <div className={css({
        display: 'flex',
        flexFlow: 'column nowrap',
        gap: '8px',
        marginBottom: '14px'
      })}>
          <HeadingLevel>
            <Heading>Filters</Heading>
          </HeadingLevel>

          <div>
            <FormControl label={() => 'Jurisdictions'}>
              <Select value={selectedJurisdictions} disabled={isLoading} placeholder="Jurisdictions" onChange={params => {
              setJurisdictionFilter(params.value);
            }} options={jurisdictions} clearable={false} multi overrides={{
              Root: {
                props: {
                  'data-testid': 'jurisdiction-select'
                }
              }
            }} />
            </FormControl>
          </div>

          <div>
            <FormControl label={() => 'Ops Lead'}>
              <Select value={selectedOpsLeads} disabled={isLoading} placeholder="Ops Lead" onChange={params => {
              setOpsLeadFilter(params.value);
            }} options={opsLeads} clearable={false} multi overrides={{
              Root: {
                props: {
                  'data-testid': 'ops-lead-select'
                }
              }
            }} />
            </FormControl>
          </div>

          <div>
            <FormControl label={() => 'Last Updated'}>
              <DatePicker placeholder="Updated between.." value={dateRangeValue} onChange={({
              date
            }) => {
              setDateFilter(Array.isArray(date) ? date : [date]);
            }} range clearable quickSelect maxDate={new Date()} overrides={{
              Root: {
                props: {
                  'data-testid': 'date-picker'
                }
              }
            }} />
            </FormControl>
          </div>

          <div>
            <FormControl label={() => 'Project Statuses'}>
              <Select value={statusFilters} disabled={isLoading} placeholder="Select 1 or more statuses" onChange={params => {
              const values = params.value.map(p => {
                return {
                  label: p.label.toString(),
                  id: String(p.id)
                };
              });
              setStatusFilter(values);
            }} options={StatusChoices} multi clearable overrides={{
              Root: {
                props: {
                  'data-testid': 'status-select'
                }
              }
            }} />
            </FormControl>
          </div>

          <ButtonRow>
            <Button testId="apply-filters" onClick={() => {
            setIsOpen(false);
            setIsLoading(true);
            doFiltering();
            setTimeout(() => {
              setIsLoading(false);
            }, 500);
          }}>
              Apply Filters
            </Button>
            <Button testId="clear-filters" onClick={() => {
            setIsOpen(false);
            clearFilters();
          }} kind="error">
              Clear Filters
            </Button>
          </ButtonRow>
        </div>
      </Drawer>
    </div>;
};
export const RequestAppBody = () => {
  const [css] = useStyletron();
  return <main data-testid="request-app">
      <HeadingLevel>
        <div className={css({
        display: 'flex',
        flexFlow: 'row nowrap',
        justifyContent: 'space-between',
        '@media screen and (max-width: 768px)': {
          flexFlow: 'column wrap',
          gap: '12px',
          margin: '24px 0'
        }
      })}>
          <Heading>Project Assignments</Heading>
        </div>
        <div className={css({
        display: 'flex',
        flexFlow: 'row nowrap',
        '@media screen and (max-width: 768px)': {
          flexFlow: 'column wrap',
          gap: '12px',
          margin: '24px 0'
        }
      })}>
          <AssignmentBody />
        </div>
      </HeadingLevel>
    </main>;
};
const RequestApp = ({
  site
}) => {
  return <RequestContextProvider site={site}>
      <RequestAppBody />
    </RequestContextProvider>;
};
export default RequestApp;